import { Activity, BarChart3, CalendarClock, CreditCard, FileDown, Globe, LineChart, Plug, Rocket, StoreIcon, Tags, TrendingUp, Users, type LucideIcon } from "lucide-react";

/**
 * The sidebar, shaped like legacy's: a short list of SECTIONS, each holding the pages
 * that belong together. `ecom_code/client/src/components/nav-data.jsx` files Source
 * Attribution under "Overview", the campaign reports under "Perf", and the landing-page
 * reports under "Website Analysis" — a merchant who knows where a report lives in the old
 * dashboard finds it in the same place here.
 *
 * A section with ONE page and no group of its own is a direct link (`to`), not a
 * collapsible with a single child — legacy renders those the same way, and a disclosure
 * triangle that reveals exactly one item is a click that buys nothing.
 *
 * ONLY THE FIVE VERIFIED REPORTS APPEAR, plus Store Health. Owner decision, 2026-09-02:
 * the other ~89 legacy endpoints are deferred. A nav item for a report `apps/api` does not
 * serve would 404 for every merchant who clicked it.
 */

import type { MemberRole } from "./team.js";

export interface NavLeaf {
  /** The path, WITHOUT a leading slash — the shell adds it. */
  readonly to: string;
  readonly label: string;
  readonly description: string;
}

export interface NavSection {
  readonly label: string;
  readonly icon: LucideIcon;
  /** A section that IS a page. Mutually exclusive with `items`. */
  readonly to?: string;
  readonly description?: string;
  readonly items?: readonly NavLeaf[];
  /**
   * Restrict the section to these roles. Absent means everyone sees it. The API enforces
   * the same limit — this only spares an analyst a menu item that would 403.
   */
  readonly roles?: readonly MemberRole[];
  /**
   * Setup is a first-run checklist, not a settings page. Once the store is live it has
   * nothing left to show, so it is HIDDEN rather than left to silently forward the user to
   * a report — which is what made "I clicked Setup and landed on a report" so confusing.
   */
  readonly hideWhenSetupComplete?: boolean;
  /**
   * Gate this section behind a runtime feature flag (from `/config.json`, set per
   * environment). Absent means always on. Config-driven rather than an `if (env === …)`
   * check (doc 06 §5): dev enables `billing`, uat/prod leave it off until it ships.
   */
  readonly feature?: string;
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    label: "Setup",
    icon: Rocket,
    to: "onboarding",
    description: "Finish connecting your store.",
    hideWhenSetupComplete: true,
    roles: ["owner", "admin"],
  },
  {
    label: "Connect a store",
    icon: StoreIcon,
    to: "settings/add-store",
    description: "Add another Shopify store to this workspace.",
    roles: ["owner", "admin"],
  },
  {
    label: "Store Health",
    icon: Activity,
    to: "health",
    description: "Is tracking working, and is your data arriving?",
    roles: ["owner", "admin"],
  },
  {
    label: "Overview",
    icon: BarChart3,
    items: [
      {
        to: "reports/source-attribution",
        label: "Source Attribution Analysis",
        description: "Spend, revenue and ROAS by traffic source.",
      },
    ],
  },
  {
    label: "Perf",
    icon: TrendingUp,
    items: [
      {
        to: "reports/meta-campaign-attribution",
        label: "Meta Campaign Attribution",
        description: "Meta campaigns, with first-click, last-click and data-driven revenue.",
      },
      {
        to: "reports/google-campaign-attribution",
        label: "Google Campaign Attribution",
        description: "Google campaigns, with first-click, last-click and data-driven revenue.",
      },
    ],
  },
  {
    /**
     * Daily Performance — legacy's own section (`nav-data.jsx`). "Campaign & Ad Pulse"
     * judges hidden winners on first-click ROAS; "Ad Pulse" on last-click.
     */
    label: "Daily Performance",
    icon: CalendarClock,
    items: [
      {
        to: "daily-performance",
        label: "Campaign & Ad Pulse",
        description: "Meta campaigns below platform ROAS that still start journeys (first-click).",
      },
      {
        to: "daily-ad-performance",
        label: "Ad Pulse",
        description: "Meta ads below platform ROAS that still close journeys (last-click).",
      },
    ],
  },
  {
    /**
     * ITS OWN SECTION, not a child of Website Analysis (owner request, 2026-09-10).
     * Legacy files it under Website Analysis; here it audits the AD setup rather than
     * site traffic, and the merchant asked for it as a distinct sidebar entry.
     */
    label: "UTM Checker",
    icon: Tags,
    items: [
      {
        to: "utm-checker",
        label: "UTM Parameter Audit",
        description: "Audit tracking parameters on active Meta & Google ads.",
      },
    ],
  },
  {
    label: "Network Analytics",
    icon: Users,
    items: [
      {
        to: "network-user-growth",
        label: "User Growth Analysis",
        description: "Day-wise cross-brand user growth and repeat buyer intelligence across all stores.",
      },
    ],
  },
  {
    label: "Website Analysis",
    icon: Globe,
    /**
     * HELD BACK FROM THE FIRST UAT RELEASE (owner, 2026-09-20), along with Billing.
     *
     * The same mechanism Billing already uses, so there is one way to do this rather than
     * two: dev lists `website-analysis` in WEB_FEATURES, uat leaves it out.
     *
     * The uat overlay ALSO drops `landing-pages` and `landing-page-trend` from
     * API_ENABLED_REPORTS. Hiding the menu alone would leave the URLs answering to anyone
     * who typed them.
     */
    feature: "website-analysis",
    items: [
      {
        to: "reports/landing-pages",
        label: "Landing Page Analysis",
        description: "Sessions, visitors and conversion by landing page.",
      },
      {
        to: "reports/landing-page-trend",
        label: "Landing Page Analysis - Trend",
        description: "The same pages, broken down week by week.",
      },
    ],
  },
  {
    /**
     * Cohort LTV — legacy's own section (`nav-data.jsx`). "Campaign Cohort LTV Analysis",
     * "Campaign Stage Analysis", "Latent Value Analysis", "Attribution Model Analysis"
     * and "Customer Journey & Orders" are ported so far — the same section legacy files
     * them under.
     */
    label: "Cohort LTV",
    icon: LineChart,
    /**
     * PULLED FROM UAT (owner, 2026-09-22), and it had to be pulled rather than simply omitted:
     * it was IN the agreed 2026-09-20 release list, so `nav.test.ts` asserted it must appear.
     * This is a changed decision, not a bug being fixed, and the test moved with it so that
     * list stays the record of what was agreed.
     *
     * The API already refused all five of these — uat's `API_ENABLED_REPORTS` lists only
     * source-attribution and the two campaign reports — so the menu was the whole leak: every
     * item rendered and then failed when clicked.
     *
     * Third user of this mechanism after `billing` and `website-analysis`: dev lists the flag
     * in WEB_FEATURES, uat leaves it out.
     */
    feature: "cohort-ltv",
    items: [
      {
        to: "cohort-ltv",
        label: "Campaign Cohort LTV Analysis",
        description: "Campaigns by creation month, with acquisition and purchaser cohorts over time.",
      },
      {
        to: "campaign-stage-analysis",
        label: "Campaign Stage Analysis",
        description: "Campaigns by funnel stage, with new/old visitor and purchaser cohorts.",
      },
      {
        to: "latent-value-analysis",
        label: "Latent Value Analysis",
        description: "Cumulative and interval purchaser growth, up to 180 days after acquisition.",
      },
      {
        to: "attribution-model-analysis",
        label: "Attribution Model Analysis",
        description: "Side-by-side comparison of 5 core attribution models across channels and funnel tiers.",
      },
      {
        to: "customer-journey-orders",
        label: "Customer Journey & Orders",
        description: "Every order with its full multi-touch journey, first/last touch and conversion time.",
      },
    ],
  },
  {
    label: "My Exports",
    icon: FileDown,
    to: "exports",
    description: "Files you have exported, ready to download.",
  },
  {
    label: "Integrations",
    icon: Plug,
    to: "settings/integrations",
    description: "Connect your Meta and Google ad accounts.",
    roles: ["owner", "admin"],
  },
  {
    label: "Billing",
    icon: CreditCard,
    to: "settings/billing",
    description: "Your plan, and how to change it.",
    roles: ["owner"],
    feature: "billing",
  },
  {
    label: "Team",
    icon: Users,
    to: "settings/team",
    description: "Invite people and manage roles.",
    roles: ["owner", "admin"],
  },
];

/** Every page, flattened — for the landing grid and for "does this route exist". */
export const NAV_LEAVES: readonly NavLeaf[] = NAV_SECTIONS.flatMap((section) =>
  section.items ?? (section.to === undefined ? [] : [{ to: section.to, label: section.label, description: section.description ?? "" }]),
);

/**
 * The sections THIS viewer should see, given their role and whether the store is set up.
 *
 * Pure and unit-tested, so the two visibility rules — role-gating (Team is owner/admin
 * only) and setup-completion (Setup disappears once the store is live) — are provable
 * without rendering a sidebar.
 */
export function visibleNavSections(
  sections: readonly NavSection[],
  viewer: { readonly role: MemberRole; readonly setupComplete: boolean; readonly features: ReadonlySet<string> },
): readonly NavSection[] {
  return sections.filter((section) => {
    if (section.roles !== undefined && !section.roles.includes(viewer.role)) return false;
    if (section.hideWhenSetupComplete === true && viewer.setupComplete) return false;
    if (section.feature !== undefined && !viewer.features.has(section.feature)) return false;
    return true;
  });
}
